// WHAT A CONTENT WRITER MAY SEE — the partner's side of it.
//
// Two panes of the same decision. On the left, what is being granted: every retainer and
// every standalone job, with a checkbox. On the right, WHAT THAT REACHES — the resolved
// list, fetched from the server after each save.
//
// ⚠ THE PREVIEW IS THE SERVER'S ANSWER, NOT THE BROWSER'S. It calls the same resolver the
// writer's own screen calls (`grants.resolveFor`), so the partner is looking at the
// literal list the writer will get. Re-deriving "a retainer grant covers its topics" here
// would be a second implementation of the rule, and the day the two disagreed, the one on
// screen would be the wrong one.
//
// Only a retainer's TOPICS come with it; a standalone job is granted on its own. Both
// facts are stated beside the controls rather than left to be discovered.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { usersAdminApi, retainersApi, jobsApi } from '../../api';
import Modal from '../../components/Modal';
import AsyncBoundary, { ErrorText } from '../../components/Loader';

// A checkbox row. Deliberately plain: this dialog is a list of names, and the only thing
// that matters about each is whether it is ticked.
const Row = ({ checked, onToggle, title, subtitle }) => (
  <label className="row grants-row">
    <input type="checkbox" checked={checked} onChange={onToggle} />
    <span>
      {title}
      {subtitle && <span className="muted"> · {subtitle}</span>}
    </span>
  </label>
);

export default function GrantsModal({ user, onClose, onSaved }) {
  const [saveKey, setSaveKey] = useState(0);
  // The pickable world plus the current grants plus the resolved preview, in one fetch so
  // the three cannot describe different moments.
  const { data, loading, error } = useAsync(() => Promise.all([
    retainersApi.list(),
    jobsApi.list({ standalone: true }),
    usersAdminApi.grants(user.id),
    usersAdminApi.grantsPreview(user.id),
  ]), [user.id, saveKey]);

  const [retainers, standaloneJobs, granted, preview] = data || [[], [], null, null];
  const [picked, setPicked] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Seeded from the server once the fetch lands, then owned locally until Save.
  const current = picked || (granted
    ? { retainers: new Set(granted.retainerIds), jobs: new Set(granted.jobIds) }
    : { retainers: new Set(), jobs: new Set() });

  const toggle = (kind, id) => () => {
    const next = {
      retainers: new Set(current.retainers),
      jobs: new Set(current.jobs),
    };
    if (next[kind].has(id)) next[kind].delete(id);
    else next[kind].add(id);
    setPicked(next);
  };

  const submit = async () => {
    try {
      await usersAdminApi.setGrants(user.id, [...current.retainers], [...current.jobs]);
      setPicked(null);
      setSaveKey((k) => k + 1); // re-fetch, so the preview is the server's new answer
      onSaved();
    } catch (e) {
      setSaveError(e);
    }
  };

  const nothingGranted = preview
    && preview.retainers.length === 0 && preview.jobs.length === 0;

  return (
    <Modal title={he.users.grantsTitle(user.name)} onClose={onClose} onSave={submit}>
      <AsyncBoundary loading={loading} error={error}>
        <h3>{he.users.grantsRetainers}</h3>
        {retainers.map((r) => (
          <Row
            key={r.id}
            checked={current.retainers.has(r.id)}
            onToggle={toggle('retainers', r.id)}
            title={r.title}
            subtitle={r.client_name}
          />
        ))}

        <h3 className="grants-head">{he.users.grantsJobs}</h3>
        {standaloneJobs.map((j) => (
          <Row
            key={j.id}
            checked={current.jobs.has(j.id)}
            onToggle={toggle('jobs', j.id)}
            title={j.title}
            subtitle={j.client_name}
          />
        ))}

        {/* The server's own answer, not a second copy of the rule. */}
        <h3 className="grants-head">{he.users.grantsPreview}</h3>
        {nothingGranted && <p className="muted">{he.users.grantsNone}</p>}
        {preview && preview.retainers.map((r) => (
          <div key={r.id} className="grants-item">
            <strong>{r.title}</strong>
            <span className="muted"> · {r.client_name} · {he.users.grantsTopics(r.jobs.length)}</span>
            <div className="grants-topics">
              {r.jobs.map((j) => <div key={j.id} className="muted">{j.title}</div>)}
            </div>
          </div>
        ))}
        {preview && preview.jobs.length > 0 && (
          <div className="grants-standalone">
            <strong>{he.users.grantsStandalone}</strong>
            {preview.jobs.map((j) => (
              <div key={j.id} className="muted">{j.title} · {j.client_name}</div>
            ))}
          </div>
        )}
      </AsyncBoundary>

      {saveError && <ErrorText error={saveError} />}
    </Modal>
  );
}
